import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface PageHeaderBackLink {
  to: string;
  label: string;
}

interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: IconDefinition;
  /** Right-aligned actions (buttons, toggles). */
  actions?: React.ReactNode;
  /** Small metadata line under the title (created date, counts, ...). */
  meta?: React.ReactNode;
  backLink?: PageHeaderBackLink;
  /** Hero headers on the landing page centre themselves. */
  align?: 'start' | 'center';
  className?: string;
}

/**
 * The single page-header primitive: title, optional subtitle, optional
 * right-aligned actions and an optional back link. Replaces the ad-hoc
 * `<h1>` + `Row/Col text-end` clusters each page used to build by hand.
 */
const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  icon,
  actions,
  meta,
  backLink,
  align = 'start',
  className = '',
}) => (
  <header className={`page-head page-head--${align} ${className}`.trim()}>
    {backLink && (
      <Link to={backLink.to} className="page-head__back">
        <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
        {backLink.label}
      </Link>
    )}
    <div className="page-head__row">
      <div className="page-head__text">
        <h1 className="page-head__title">
          {icon && <FontAwesomeIcon icon={icon} className="page-head__icon" aria-hidden="true" />}
          <span>{title}</span>
        </h1>
        {subtitle && <p className="page-head__subtitle">{subtitle}</p>}
        {meta && <div className="page-head__meta">{meta}</div>}
      </div>
      {actions && <div className="page-head__actions">{actions}</div>}
    </div>
  </header>
);

export default PageHeader;
